// © Microsoft Corporation. All rights reserved.

import { Content, TypingUpdates, User } from "@msnews/social-data-service";
import { avatarFallback, placeholderImages, typing_animation } from "@msnews/social-utils";
import React from "react";
import { connect } from "react-redux";
import { String } from "typescript-string-operations";
import { SocialCommentLocalizedStrings } from "../../SocialComment.localizedStrings";
import { SocialCommentState } from "../../SocialComment.state";
import { TypingUsersClassNameContract } from "./TypingUsers.classnames";

export interface TypingUsersProps {
    me?: User,
    typingUpdates?: TypingUpdates;
    managedClasses: TypingUsersClassNameContract;
    updateTypers: () => void;
    listenForTypers: (id: string, market: string, content: Content) => void;
    t: SocialCommentLocalizedStrings;
    content: Content;
    noText?: boolean;
    id?: string;
    market?: string;
}

interface TypingUsersState {
    timer?: any;
}

export class TypingUsers extends React.Component<TypingUsersProps, TypingUsersState> {

    constructor(props) {
        super(props);
    }

    componentDidMount() {
        this.props.listenForTypers(this.props.id, this.props.market, this.props.content);
        this.setState({timer: setInterval(this.updateTypers.bind(this), 1000)});
    }

    componentWillUnmount() {
        if (this.state && this.state.timer) {
            clearInterval(this.state.timer);
        }
    }

    updateTypers() {
        const { typingUpdates, updateTypers } = this.props;
        if (typingUpdates) {
            updateTypers();
        }
    }

    render() {
        const { me, typingUpdates, managedClasses, t, noText } = this.props;
        if (!typingUpdates) {
            return null;
        }

        const myId = me && me.id;
        const { displayedTypers, numTypers } = typingUpdates;

        if (!displayedTypers || !numTypers ||
      numTypers === 0 ||
      numTypers < displayedTypers.length ||
      displayedTypers.length > 3 ||
      (numTypers < 3 && numTypers !== displayedTypers.length)) {
            return null; // either there's no one typing, or we have inconsistent props passed to us
        } else {
            return (
                <div className={managedClasses.typingUsersContainer} >
                    <TypingUsersImages displayedTypers={ ...displayedTypers } t={t} />
                    { !noText && <TypingUsersText displayedTypers={displayedTypers} numTypers={numTypers} me={myId} t={t}/> }
                    <i className="cwt-icon cwt-typing-animation" > {typing_animation} </i>
                </div>
            );
        }
    }
}

// Create DOM elements
function TypingUsersImages(props) {
    const { displayedTypers, t } = props;
    const className = "cwt-typing-user-images image-count-" + displayedTypers.length;
    const images = displayedTypers.map((typer, index) => <TypingUserImage displayedTyper={typer} key={typer.id} index={index} t={t} />);
    return (
        <div className={className}>
            {images}
        </div>
    );
}

function TypingUserImage(props) {
    const { displayedTyper, index, t } = props;
    const className = "cwt-typing-user-image image" + index;
    return (
        <div className={className}>
            <img alt={t.AriaLabelsProfilePicture} src={displayedTyper.profileImageUrl || placeholderImages.avatar} onError={(e)=>{ avatarFallback(e); }}></img>
        </div>
    );
}

function TypingUsersText(props) {
    const { me, displayedTypers, numTypers, t} = props;
    let text;
    if (numTypers === 1) {
        const user1 = typingUserName({ displayedTyper: displayedTypers[0], me, t });
        text = me === displayedTypers[0].id
            ? t.YouTypingUserInfo
            : String.Format(t.SingleTypingUserInfo, { user1 });

    } else if (numTypers === 2) {
        const user1 = typingUserName({ displayedTyper: displayedTypers[0], me, t });
        const user2 = typingUserName({ displayedTyper: displayedTypers[1], me, t });
        text = String.Format(t.DoubleTypingUserInfo, { user1, user2 });

    } else if (numTypers >= 3 && numTypers <= displayedTypers.length) {
        const user1 = typingUserName({ displayedTyper: displayedTypers[0], me, t });
        const user2 = typingUserName({ displayedTyper: displayedTypers[1], me, t });
        const user3 = typingUserName({ displayedTyper: displayedTypers[2], me, t });
        text = String.Format(t.TripleTypingUserInfo, { user1, user2, user3 });

    } else if (numTypers > 3) {
        const user1 = typingUserName({ displayedTyper: displayedTypers[1], me, t });
        const user2 = typingUserName({ displayedTyper: displayedTypers[2], me, t });
        text = String.Format(t.PeopleTypingUserInfo, { user1, user2, numberOfUsers: numTypers - 2 });
    }
    return (
        <div
            className="cwt-typing-users-text"
            dangerouslySetInnerHTML={{ __html: text }}
        />
    );
}

function typingUserName(props) {
    const { me, displayedTyper, t } = props;
    return `<span className="cwt-typer-name">${me === displayedTyper.id ? t.UserPlaceHolder : displayedTyper.name}</span>`;
}

const mapStateToProps = (state: SocialCommentState, props: TypingUsersProps) => {
    const content: Content = props.content;
    const typingUpdates: TypingUpdates = content && content.typingUpdates;
    const me: User = state.me;
    const id: string = state.id;
    const market: string = state.market;
    return {
        typingUpdates,
        me,
        id,
        market
    };
};

export default connect(mapStateToProps)(TypingUsers);